import { useEffect, useState } from 'react';
import { getAdminPrivilegesApiService } from '../api/getAdminPrivilegesApiService';
import type { AdminPrivileges } from '../types/adminPrivilegeTypes';
import { ADMIN_MODULES, tAdmin } from '../constants/adminPrivilegeConstants';

function getEnabledItems(
  privileges: AdminPrivileges,
  module: (typeof ADMIN_MODULES)[number]
) {
  return module.items.filter((item) => privileges[item.key]);
}

interface AdminPrivilegesViewProps {
  userId: string;
}

const AdminPrivilegesView = ({ userId }: AdminPrivilegesViewProps) => {
  const [privileges, setPrivileges] = useState<AdminPrivileges | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    getAdminPrivilegesApiService(userId)
      .then((result) => {
        if (result) {
          setPrivileges(result);
        } else {
          setError('Nie udało się załadować uprawnień.');
        }
      })
      .catch(() => setError('Nie udało się załadować uprawnień.'))
      .finally(() => setIsLoading(false));
  }, [userId]);

  const modulesWithEnabled = privileges
    ? ADMIN_MODULES.map((module) => ({
        id: module.id,
        title: module.title,
        enabled: getEnabledItems(privileges, module),
      })).filter((module) => module.enabled.length > 0)
    : [];

  return (
    <div className="flex flex-col gap-6 m-[6px]">
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
        <p
          className="text-[18px] font-medium text-on-surface-variant"
          aria-label="Uprawnienia"
          data-testid="view-admin-permissions-title"
        >
          Uprawnienia
        </p>

        {isLoading ? (
          <div
            className="flex flex-col gap-6"
            data-testid="view-admin-permissions-loading"
          >
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex flex-col gap-2">
                <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: 4 - (i % 2) }).map((_, j) => (
                    <div
                      key={j}
                      className="h-8 bg-gray-200 rounded-[8px] animate-pulse"
                      style={{ width: `${72 + ((j * 16) % 48)}px` }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <p
            className="text-bodyMedium text-on-surface-variant"
            data-testid="view-admin-permissions-error"
          >
            {error}
          </p>
        ) : modulesWithEnabled.length === 0 ? (
          <p
            className="text-bodyMedium text-on-surface-variant"
            data-testid="view-admin-permissions-empty"
          >
            Brak danych do wyświetlenia.
          </p>
        ) : (
          <div
            className="flex flex-col gap-8"
            data-testid="view-admin-permissions-content"
          >
            {modulesWithEnabled.map((module) => (
              <div key={module.id} className="flex flex-col gap-3">
                <p className="text-bodySmall font-normal text-on-surface-variant">
                  {tAdmin(module.title)}
                </p>
                <div className="flex flex-wrap gap-2">
                  {module.enabled.map((item) => (
                    <span
                      key={item.key}
                      className="inline-flex items-center justify-center h-8 px-3 rounded-[8px] bg-information-90 text-information-30 text-labelLarge"
                      data-testid={`view-admin-permission-${item.key}`}
                    >
                      {tAdmin(item.label)}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPrivilegesView;
